import { useEffect, useRef, useState, type ReactElement } from 'react';
import { Modal } from './Modal';
import './ImagePreview.css';

export interface ImagePreviewProps {
  /** The image to open on. With `images`, the one at `index` wins. */
  src: string;
  /** Every image of the set, in order. More than one arms the navigation. */
  images?: string[];
  /** Where in `images` to start. */
  index?: number;
  alt?: string;
  onClose: () => void;
}

/** How far a horizontal drag must travel (px) before it reads as a swipe. */
const SWIPE_MIN_PX = 40;

/**
 * Shared full-size image viewer for any Ember surface. A single image views
 * as before; a set adds the ways through it — swipe on touch, chevrons and
 * arrow keys elsewhere — wrapping at both ends.
 */
export function ImagePreview({
  src, images, index = 0, alt = '', onClose,
}: ImagePreviewProps): ReactElement {
  const set = images && images.length > 1 ? images : null;
  const [at, setAt] = useState(() => (set ? Math.min(Math.max(index, 0), set.length - 1) : 0));
  const touchX = useRef<number | null>(null);

  const step = (delta: number): void => {
    if (!set) return;
    setAt((n) => (n + delta + set.length) % set.length);
  };

  useEffect(() => {
    if (!set) return undefined;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'ArrowLeft') step(-1);
      if (e.key === 'ArrowRight') step(1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // `set` only flips between null and non-null with the caller's list.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [set]);

  return (
    <Modal
      onClose={onClose}
      labelledBy="ember-image-preview-title"
      className="image-preview"
      focusDialog
    >
      <h2 id="ember-image-preview-title" className="image-preview-title">Image preview</h2>
      <img
        className="image-preview-image"
        src={set ? set[at] : src}
        alt={alt}
        onTouchStart={(e) => { touchX.current = e.touches[0]?.clientX ?? null; }}
        onTouchEnd={(e) => {
          if (!set || touchX.current === null) return;
          const dx = (e.changedTouches[0]?.clientX ?? touchX.current) - touchX.current;
          touchX.current = null;
          if (dx <= -SWIPE_MIN_PX) step(1);
          else if (dx >= SWIPE_MIN_PX) step(-1);
        }}
      />
      {set && (
        <>
          <button type="button" className="image-preview-nav is-prev" data-log="photo_preview_nav" data-log-state="prev" onClick={() => step(-1)} aria-label="Previous image">
            <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="m14.5 6-6 6 6 6" />
            </svg>
          </button>
          <button type="button" className="image-preview-nav is-next" data-log="photo_preview_nav" data-log-state="next" onClick={() => step(1)} aria-label="Next image">
            <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="m9.5 6 6 6-6 6" />
            </svg>
          </button>
          <span className="image-preview-count" aria-live="polite">{`${at + 1} / ${set.length}`}</span>
        </>
      )}
      <button type="button" className="image-preview-close" onClick={onClose} aria-label="Close image preview">
        <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
          <path d="M6 6l12 12M18 6 6 18" />
        </svg>
      </button>
    </Modal>
  );
}
